import { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { User, Pencil, X, Check } from 'lucide-react';
import { getWinProbability } from './utils';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamMemberCard({ member, onUpdate }) {
  const wp = getWinProbability(member.avg_deal_score);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(
    member.call_limit_override == null ? '' : String(member.call_limit_override)
  );
  const [saving, setSaving] = useState(false);

  // Skip editor for managers (they should set their own plan, not be capped by self)
  const canEdit = member.role === 'sales_rep';

  const save = async (newValue) => {
    setSaving(true);
    try {
      const body = newValue === '' || newValue == null
        ? { call_limit_override: null }
        : { call_limit_override: parseInt(newValue, 10) };
      if (body.call_limit_override != null && (Number.isNaN(body.call_limit_override) || body.call_limit_override < 0)) {
        toast.error('Limit must be a non-negative number or empty to clear.');
        return;
      }
      const res = await fetch(`${API}/api/teams/members/${member.user_id}/call-limit`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success(data.message);
      setEditing(false);
      onUpdate?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border/50 hover:border-primary/30 transition-colors" data-testid={`team-member-${member.user_id}`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
            <User className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate">{member.name}</p>
            <p className="text-sm text-muted-foreground capitalize">
              {member.role.replace('_', ' ')}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <p className="text-xs text-muted-foreground">Calls</p>
            <p className="font-heading text-xl font-bold text-foreground">{member.total_calls}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Avg Win %</p>
            <p className={`font-heading text-xl font-bold ${wp.color}`}>{wp.pct}%</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">SQLs</p>
            <p className="font-heading text-xl font-bold text-success">{member.sql_count}</p>
          </div>
        </div>

        {canEdit && (
          <div className="border-t border-border/30 pt-3">
            {editing ? (
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground whitespace-nowrap">Call limit/mo:</label>
                <input
                  type="number"
                  min="0"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Plan default"
                  className="flex-1 min-w-0 px-2 py-1 text-xs rounded border border-border bg-background focus:outline-none focus:border-primary"
                  data-testid={`limit-input-${member.user_id}`}
                  autoFocus
                />
                <button
                  onClick={() => save(value)}
                  disabled={saving}
                  className="p-1 rounded text-success hover:bg-success/10 disabled:opacity-50"
                  title="Save"
                  data-testid={`limit-save-${member.user_id}`}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditing(false); setValue(member.call_limit_override == null ? '' : String(member.call_limit_override)); }}
                  disabled={saving}
                  className="p-1 rounded text-muted-foreground hover:bg-secondary"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors w-full"
                data-testid={`limit-edit-${member.user_id}`}
              >
                <Pencil className="w-3 h-3" />
                <span>
                  Monthly call limit:{' '}
                  <strong className="text-foreground">
                    {member.call_limit_override == null ? 'Plan default' : member.call_limit_override}
                  </strong>
                </span>
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
